import { defineStore } from 'pinia'
import { ui } from "../assets/data/resource"
import storage from "../services/localStorage"

const locales = { th: 'th-TH', en: 'en-US' }

export const useLocalStore = defineStore('locale', {
    state: () => ({
        locale: storage.getItem('locale', process.env.VUE_APP_LOCALE || 'en'),
        monthNamesCached: {
            en: [],
            th: []
        },
        dateFormatter: null,
        dateTimeFormatter: null,
        currencyFormatter: null,
        yearFormatter: null,
        intFormatter: null
    }),
    getters: {
        monthNames: (state) => {
            if (state.monthNamesCached[state.locale].length == 0) {
                let lc = locales[state.locale]
                let result = []

                const formatter = new Intl.DateTimeFormat(lc, { month: 'short', timeZone: 'UTC' })

                for (let i = 1; i <= 12; i++) {
                    const mm = i < 10 ? '0' + i : i
                    result.push(formatter.format(new Date('2023-' + mm + '-01T00:00:00+00:00')))
                }

                state.monthNamesCached[state.locale] = result
            }

            return state.monthNamesCached[state.locale]
        },
        password_change_complete: (state) => ui.password_change_complete[state.locale],
        previewFile: (state) => ui.previewFile[state.locale],
        selectFile: (state) => ui.selectFile[state.locale],
        clearFile: (state) => ui.clearFile[state.locale],
        ok: (state) => ui.ok[state.locale],
        home: (state) => ui.home[state.locale],
        register: (state) => ui.register[state.locale],
        register_hint: (state) => ui.register_hint[state.locale],
        register_form_hint: (state) => ui.register_form_hint[state.locale],
        register_submit: (state) => ui.register_submit[state.locale],
        register_sent: (state) => ui.register_sent[state.locale],
        external_register: (state) => ui.external_register[state.locale],
        login: (state) => ui.login[state.locale],
        logoff: (state) => ui.logoff[state.locale],
        settings: (state) => ui.settings[state.locale],
        developer: (state) => ui.developer[state.locale],
        noData: (state) => ui.noData[state.locale],
        loading: (state) => ui.loading[state.locale],
        email: (state) => ui.email[state.locale],
        password: (state) => ui.password[state.locale],
        confirmPassword: (state) => ui.confirmPassword[state.locale],
        passwordNotMatched: (state) => ui.passwordNotMatched[state.locale],
        external_login: (state) => ui.external_login[state.locale],
        profile: (state) => ui.profile[state.locale],
        displayName: (state) => ui.displayName[state.locale],
        language: (state) => ui.language[state.locale],
        theme: (state) => ui.theme[state.locale],
        submit: (state) => ui.submit[state.locale],
        changePicture: (state) => ui.changePicture[state.locale],
        add: (state) => ui.add[state.locale],
        refresh: (state) => ui.refresh[state.locale],
        search: (state) => ui.search[state.locale],
        searchByKeyword: (state) => ui.searchByKeyword[state.locale],
        edit: (state) => ui.edit[state.locale],
        cancel: (state) => ui.cancel[state.locale],
        confirmDelete: (state) => ui.confirmDelete[state.locale],
        formInvalid: (state) => ui.formInvalid[state.locale],
        requireMsg: (state) => ui.requireMsg[state.locale],
        lengthMsg: (state) => ui.lengthMsg[state.locale],
        invalidNumMsg: (state) => ui.invalidNumMsg[state.locale],
        error: (state) => ui.error[state.locale],
        password_recovery: (state) => ui.password_recovery[state.locale],
        toDay: (state) => ui.toDay[state.locale],
        month: (state) => ui.month[state.locale],
        months: (state) => ui.months[state.locale],
        year: (state) => ui.year[state.locale],
        week: (state) => ui.week[state.locale],
        day: (state) => ui.day[state.locale],
        remove: (state) => ui.remove[state.locale],
        noMessage: (state) => ui.noMessage[state.locale],
        userNotExit: (state) => ui.userNotExit[state.locale],
        notification: (state) => ui.notification[state.locale],
        maskRead: (state) => ui.maskRead[state.locale],
        confirmLogoff: (state) => ui.confirmLogoff[state.locale],
        close: (state) => ui.close[state.locale],
        select: (state) => ui.select[state.locale],
        signout_complete: (state) => ui.signout_complete[state.locale],
        password_reset_hint: (state) => ui.password_reset_hint[state.locale],
        password_reset_submit: (state) => ui.password_reset_submit[state.locale],
        password_reset_sent: (state) => ui.password_reset_sent[state.locale],
        password_reset_verify_hint: (state) => ui.password_reset_verify_hint[state.locale],
        notReady: (state) => ui.notReady[state.locale],
        noRecaptcha: (state) => ui.noRecaptcha[state.locale],
        err_1: (state) => ui.err_1[state.locale],
        err_2: (state) => ui.err_2[state.locale],
        err_3: (state) => ui.err_3[state.locale],
        err_4: (state) => ui.err_4[state.locale],
        err_20: (state) => ui.err_20[state.locale],
        err_21: (state) => ui.err_21[state.locale],
        err_22: (state) => ui.err_22[state.locale],
    },

    actions: {
        formatNumber(num, format, fraction) {
            if (format == 'decimal') return this.getDecimal(num)
            else if (format == 'currency') return this.getCurrency(num)
            else if (format == 'integer') return this.getInt(num)
            return this.getFloating(num, fraction)
        },
        getCurrency(num) {
            if (this.currencyFormatter == null) {
                let lc = locales[this.locale]
                this.currencyFormatter = new Intl.NumberFormat(lc, { minimumFractionDigits: 2 })
            }

            return this.currencyFormatter.format(num)
        },
        getInt(num) {
            if (this.intFormatter == null) {
                let lc = locales[this.locale]
                this.intFormatter = new Intl.NumberFormat(lc, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
            }

            return this.intFormatter.format(num)
        },
        getFloating(num, fraction) {
            return Number.parseFloat(num).toFixed(fraction);
        },
        getDecimal(num) {
            return Number.parseFloat(num).toFixed(2);
        },
        getDateTime(date) {
            if (this.dateTimeFormatter == null) {
                let lc = locales[this.locale]
                this.dateTimeFormatter = new Intl.DateTimeFormat(lc, { dateStyle: 'medium', timeStyle: 'medium' })
            }

            return this.dateTimeFormatter.format(Date.parse(date))
        },
        getDate(date) {
            if (this.dateFormatter == null) {
                let lc = locales[this.locale]
                this.dateFormatter = new Intl.DateTimeFormat(lc, { dateStyle: 'medium' })
            }

            return this.dateFormatter.format(Date.parse(date))
        },
        getMonthName(month) {
            return this.monthNames[month - 1]
        },
        getStartYearDate(y) {
            if (this.locale == 'th') {
                return new Date(y - 543, 0, 1)
            }

            return new Date(y, 0, 1)
        },
        getEndYearDate(y) {
            if (this.locale == 'th') {
                return new Date(y - 543, 11, 31)
            }

            return new Date(y, 11, 31)
        },
        getYear(date) {
            const d = new Date(date)
            const y = d.getFullYear()
            return this.getNumberAsYear(y)
        },
        getNumberAsYear(y) {
            if (this.locale == 'th') return (y + 543)
            return y
        },
        changeLocale(newLocale) {
            storage.setItem('locale', newLocale)
            this.locale = newLocale
        },
        getLocaleText(srcObj) {
            return srcObj[this.locale]
        },
        getErrorMessage(result) {
            if (!result) {
                return 'Unknow error!'
            } else if (result.errorMessage) {
                return result.errorMessage
            } else if (result.errors) {
                let err = ''

                for (const key in result.errors) {
                    err += result.errors[key] + ' '
                }

                return err
            } else if (result.code) {
                return this['err_' + result.code]
            }

            return 'Unknow error!'
        }
    }
})
